import React, { useState, useEffect } from 'react'
import { observer } from 'mobx-react-lite'
import {
  MDBContainer,
  MDBRow,
  MDBCol,
  MDBIcon,
  MDBBtn,
  MDBCard,
  MDBCardHeader,
  MDBCardBody,
  MDBBadge,
  MDBInput,
} from 'mdbreact'
import CONTRACTING from './../../../constants/contracting.constants'
import UserProfileService from './../../../shared/services/UserProfile.service'
import UserService from './../../../shared/services/User.service'
import UserContractCartService from './../../../shared/services/UserContractCart.service'
import ACMStore from './../AgentContractManager.store'
import UserContractCartFactory from './../../../shared/factories/user-contract-cart.factory'
import UserContractCartItemFactory from './../../../shared/factories/user-contract-cart-item.factory'
import { toast } from 'react-toastify'

import './OnboardingOverrideCart.scss'

const SIG_AGENT_REQUIRED = CONTRACTING.onboarding.SIG_AGENT_REQUIRED,
  CSR_REQUIRED = CONTRACTING.onboarding.CSR_REQUIRED,
  ASSOC_REQUIRED = CONTRACTING.onboarding.ASSOC_REQUIRED,
  AFFILIATE_REQUIRED = CONTRACTING.onboarding.AFFILIATE_REQUIRED

const getRequiredQuantity = () =>
  UserProfileService.isA(['affiliate-group'])
    ? AFFILIATE_REQUIRED
    : UserProfileService.isA(['associate-group'])
    ? ASSOC_REQUIRED
    : UserProfileService.isA(['csr-agent'])
    ? CSR_REQUIRED
    : SIG_AGENT_REQUIRED

const RenderCarrierTable = ({
  priority,
  Carriers,
  onAddToCart,
  selectedCarrierIds,
}) => {
  const renderPill = (indicator) => {
    switch (indicator.props.children) {
      case '$$$':
        return (
          <MDBBadge pill color="primary">
            {'Core'}
          </MDBBadge>
        )
      case '$$':
        return (
          <MDBBadge pill color="danger">
            {'Preferred'}
          </MDBBadge>
        )
      case '$':
        return (
          <MDBBadge pill color="success">
            {'Additional'}
          </MDBBadge>
        )
      default:
        return
    }
  }

  const isCarrierSelected = (Carrier) =>
    selectedCarrierIds.includes(parseInt(Carrier.id()))

  const renderSelectableRow = (Carrier, idx) => {
    return (
      <div
        className={
          'selectable-row ' + (isCarrierSelected(Carrier) ? 'row-selected' : '')
        }
        key={`selectable-cell-carrier-${Carrier.id()}`}
      >
        <div className="selectable-cell cell-name">
          <h6>{Carrier.get('c_name')}</h6>
          <div className="d-flex align-items-center">
            <small className="mr-2">
              {Carrier.renderPriorityIndicator(
                UserProfileService.getCurrentUserTypeId()
              )}
            </small>
            {renderPill(
              Carrier.renderPriorityIndicator(
                UserProfileService.getCurrentUserTypeId()
              )
            )}
          </div>
        </div>
        <div className="selectable-cell cell-descrip">
          <div>{Carrier.get('c_description')}</div>
        </div>

        <div className="selectable-cell cell-action">
          <MDBBtn
            className="toggle-carrier-btn"
            onClick={() => onAddToCart(Carrier)}
          >
            {isCarrierSelected(Carrier) ? (
              <>
                <MDBIcon icon="check" />
                &nbsp;<span>Added to Overide</span>
              </>
            ) : (
              <>
                <MDBIcon icon="cart-plus" />
                &nbsp;<span>Add to Overide</span>
              </>
            )}
          </MDBBtn>
        </div>
      </div>
    )
  }

  const renderSiblingsRow = (Carriers, idx) => {
    const setPriorityIndicator = () => {
      const indicatorArray = Carriers.map((Carrier) =>
        Carrier.renderPriorityIndicator(
          UserProfileService.getCurrentUserTypeId()
        )
      )

      const priorityIndicators = {
        $$$: [],
        $$: [],
        $: [],
      }

      // Group indicators by priority level
      indicatorArray.forEach((item) => {
        const priority = item.props.children
        if (priority in priorityIndicators) {
          priorityIndicators[priority].push(item)
        }
      })

      // Find the first available indicator based on priority level
      for (const priority of ['$$$', '$$', '$']) {
        if (priorityIndicators[priority].length > 0) {
          return priorityIndicators[priority][0]
        }
      }

      return null
    }

    return (
      <div
        className={'selectable-sibling-row '}
        key={`selectable-sibling-cell-carrier-${idx}`}
      >
        <div className="selectable-cell cell-name">
          <h6>{Carriers[0].get('c_name')}</h6>
          <div className="d-flex align-items-center">
            <small className="mr-2">{setPriorityIndicator()}</small>
            {renderPill(setPriorityIndicator())}
          </div>
        </div>
        <div className="sibling-rows">
          {Carriers.map((Carrier, idx) => {
            return (
              <div
                className={
                  'sibling-cell-row ' +
                  (isCarrierSelected(Carrier) ? 'row-selected' : '')
                }
                key={`sibling-cell-row-${Carrier.id()}-${idx}`}
              >
                <div className="selectable-cell cell-descrip">
                  {Carrier.get('c_description')}
                </div>

                <div className="selectable-cell cell-action">
                  <MDBBtn
                    className="toggle-carrier-btn"
                    onClick={() => onAddToCart(Carrier)}
                  >
                    {isCarrierSelected(Carrier) ? (
                      <>
                        <MDBIcon icon="check" />
                        &nbsp;<span>Added to Overide</span>
                      </>
                    ) : (
                      <>
                        <MDBIcon icon="cart-plus" />
                        &nbsp;<span>Add to Overide</span>
                      </>
                    )}
                  </MDBBtn>
                </div>
              </div>
            )
          })}
        </div>
      </div>
    )
  }

  if (Carriers.length === 0)
    return (
      <div className="selectable-carriers-table empty-list">
        No {priority} carriers available for selection.
      </div>
    )

  return (
    <div className="selectable-carriers-table">
      {Carriers.map((Carrier, idx) => {
        if (Array.isArray(Carrier)) return renderSiblingsRow(Carrier, idx)
        return renderSelectableRow(Carrier, idx)
      })}
    </div>
  )
}

const OnboardingOverrideCart = ({
  activeSpecs,
  onCheckout,
  onCancel,
  setHasOverrideCarriers,
  setSelectedCartItems,
}) => {
  const requiredItems = getRequiredQuantity()
  const [selectedCarrierIds, setSelectedCarrierIds] = useState([])
  const [searchTerm, setSearchTerm] = useState('')
  const [uplines, setUplines] = useState([])
  const [isLoading, setIsLoading] = useState(false)

  useEffect(() => {
    getUplines()
  }, [])

  useEffect(() => {
    const loadOverrideCarriers = async () => {
      if (ACMStore.Cart) {
        const cartId = ACMStore.Cart.id()
        if (cartId) {
          try {
            const cartData = await UserContractCartService.get(cartId)
            if (cartData && cartData.override_carrier) {
              const overrideIds = cartData.override_carrier
                .split(',')
                .map((id) => parseInt(id))
                .filter((id) => !isNaN(id))
              setSelectedCarrierIds(overrideIds)
            }
          } catch (error) {
            console.error('Error loading override carriers:', error)
          }
        }
      }
    }
    loadOverrideCarriers()
  }, [])

  const getUplines = async () => {
    const upline_agents = await UserService.getUpline(
      UserProfileService.getUserId()
    )
    setUplines(upline_agents.map((upline) => `${upline.id}`))
  }

  const isCarrierSelectable = (Carrier) => {
    if (parseInt(Carrier.get('c_active')) !== 1) return false

    if (Carrier.get('visibility')?.onboarding?.enable) {
      const usertype_id = parseInt(UserProfileService.getCurrentUserTypeId())

      // Check commission visibility
      if (Carrier.get('visibility')?.onboarding?.commission?.enable) {
        let userDetails = UserProfileService.getUserDetails()
        if (
          Carrier.get('visibility')?.onboarding?.commission?.[
            userDetails.commission_id
          ]
        ) {
          return false
        }
      }

      // Check usertype visibility
      if (
        [225].includes(usertype_id) &&
        !Carrier.get('visibility')?.onboarding?.csr_agent
      )
        return false
      if (
        [111, 133].includes(usertype_id) &&
        !Carrier.get('visibility')?.onboarding?.affiliate_agent
      )
        return false
      if (
        [223, 224].includes(usertype_id) &&
        !Carrier.get('visibility')?.onboarding?.associate_agent
      )
        return false
      if (
        [
          91, 92, 149, 37, 35, 38, 90, 93, 130, 129, 226, 227, 228, 219, 36,
          222,
        ].includes(usertype_id) &&
        !Carrier.get('visibility')?.onboarding?.sig_agent
      )
        return false
    } else {
      return false
    }

    // Check disabled uplines
    if (
      (Carrier.get('disabled_uplines') || []).includes(
        UserProfileService.get('u_upline_id')
      )
    ) {
      return false
    }

    if (Carrier.get('visibility')?.onboarding?.upline_ids?.length > 0) {
      const disabled_carrier = Carrier.get(
        'visibility'
      ).onboarding.upline_ids.some((upline_id) => uplines.includes(upline_id))
      if (disabled_carrier) return false
    }

    // Apply search filter
    if (searchTerm) {
      const carrierName = Carrier.get('c_name') || ''
      return carrierName.toLowerCase().includes(searchTerm.toLowerCase())
    }

    return true
  }

  const loadSelectableCarriers = () => {
    const CarrierGroups = {
      core: [],
      preferred: [],
      additional: [],
    }

    ;(ACMStore?.Carriers || [])
      .filter(isCarrierSelectable)
      .forEach((Carrier) => {
        const carrierType = Carrier.getPriority(
          activeSpecs,
          UserProfileService.get('usertype_id')
        )
        CarrierGroups[carrierType].push(Carrier)
      })

    return CarrierGroups
  }

  const organizeSiblings = (Carriers) => {
    const CarriersById = {},
      LinkedSiblings = {}
    Carriers.forEach((Carrier) => (CarriersById[Carrier.id()] = Carrier))

    for (let carrierId in CarriersById) {
      if (
        CarriersById.hasOwnProperty(carrierId) &&
        CarriersById[carrierId].get('sibling_ids')
      )
        LinkedSiblings[carrierId] = Array.isArray(
          CarriersById[carrierId].get('sibling_ids')
        )
          ? CarriersById[carrierId].get('sibling_ids')
          : JSON.parse(CarriersById[carrierId].get('sibling_ids'))

      if (
        LinkedSiblings.hasOwnProperty(carrierId) &&
        Array.isArray(LinkedSiblings[carrierId])
      ) {
        LinkedSiblings[carrierId] = LinkedSiblings[carrierId]
          .map((siblingId) => {
            let Sibling
            if (
              CarriersById.hasOwnProperty(siblingId) &&
              CarriersById[siblingId]
            ) {
              Sibling = CarriersById[siblingId]
              delete CarriersById[siblingId]
              return Sibling
            }
            return Sibling
          })
          .filter((Carrier) => Carrier)

        if (LinkedSiblings[carrierId].length === 0) {
          delete LinkedSiblings[carrierId]
        } else {
          LinkedSiblings[carrierId].unshift(CarriersById[carrierId])
          delete CarriersById[carrierId]
        }
      }
    }

    return Object.values(CarriersById)
      .concat(Object.values(LinkedSiblings))
      .sort((CarrierA, CarrierB) => {
        CarrierA = Array.isArray(CarrierA) ? CarrierA[0] : CarrierA
        CarrierB = Array.isArray(CarrierB) ? CarrierB[0] : CarrierB
        return `${CarrierA.get('c_name')}`.localeCompare(
          `${CarrierB.get('c_name')}`,
          'en'
        )
      })
  }

  const organizePrioritySiblings = ({ core, preferred, additional }) => {
    return {
      core: organizeSiblings(core),
      preferred: organizeSiblings(preferred),
      additional: organizeSiblings(additional),
    }
  }

  const handleAddToCart = (Carrier) => {
    const carrierId = parseInt(Carrier.id())
    const isSelected = selectedCarrierIds.includes(carrierId)

    if (isSelected) {
      setSelectedCarrierIds((prev) => prev.filter((id) => id !== carrierId))
    } else {
      setSelectedCarrierIds((prev) => [...prev, carrierId])
    }
  }

  const handleResetSelection = async () => {
    if (
      window.confirm('Are you sure you want to reset the override selection?')
    ) {
      setIsLoading(true)
      try {
        await UserContractCartService.update(ACMStore.Cart?.id(), {
          override_carrier: null,
        })
        setSelectedCarrierIds([])
        setHasOverrideCarriers(false)
        ACMStore.OverrideCartItems = []
        setSelectedCartItems([])
        toast.success('Override selection has been reset.', {
          position: toast.POSITION.TOP_RIGHT,
        })
        onCancel()
      } catch (error) {
        console.error('Error resetting override selection:', error)
        toast.error('Failed to reset override selection. Please try again.', {
          position: toast.POSITION.TOP_RIGHT,
        })
      } finally {
        setIsLoading(false)
      }
    }
  }

  const handleSaveSelection = async () => {
    if (selectedCarrierIds.length < requiredItems) {
      toast.error(`Please select at least ${requiredItems} carriers.`, {
        position: toast.POSITION.TOP_RIGHT,
      })
      return
    }

    setIsLoading(true)

    try {
      // Clear existing cart items first
      await clearExistingCartItems()

      // Add selected carriers to cart using the same pattern as the original onboarding cart
      const selectedCarriers = selectedCarrierIds
        .map((carrierId) => {
          const carrier = ACMStore.Carriers.find(
            (c) => parseInt(c.id()) === carrierId
          )
          return carrier
        })
        .filter(Boolean)

      // Create cart items for selected carriers and add to ACMStore.OverrideCartItems
      const cartItems = selectedCarriers.map((carrier) => {
        const cartItem = UserContractCartItemFactory.create({
          cart_id: ACMStore.Cart?.id(),
          item_status: 'DRAFT',
          carrier_id: carrier.id(),
        })
        return cartItem
      })

      // Update both ACMStore and SelectedCartItems
      ACMStore.OverrideCartItems = cartItems
      setSelectedCartItems(cartItems)

      // Set hasOverrideCarriers flag
      setHasOverrideCarriers(true)

      if (selectedCarriers) {
        const Cart = await UserContractCartFactory.findById(ACMStore.Cart?.id())
        const overrideCarrierIds = selectedCarriers.map((carrier) =>
          carrier.id()
        )
        if (parseInt(Cart.get('cart_onboarding')) === 1) {
          await UserContractCartService.update(ACMStore.Cart?.id(), {
            override_carrier: overrideCarrierIds.join(',').toString(),
          })
        }
      }

      // Track the override action
      UserProfileService.track({
        event_type: 'contracting-cart.override.success',
        event_descrip: 'BST user successfully overrode carrier selection',
        payload: {
          selected_carrier_ids: selectedCarrierIds,
          required_quantity: requiredItems,
          total_selected: selectedCarrierIds.length,
        },
      })

      toast.success('Carrier selection override completed successfully!', {
        position: toast.POSITION.TOP_RIGHT,
      })

      // Go back to previous screen
      onCancel()
    } catch (error) {
      console.error('Error saving carrier selection:', error)
      toast.error('Failed to save carrier selection. Please try again.', {
        position: toast.POSITION.TOP_RIGHT,
      })
    } finally {
      setIsLoading(false)
    }
  }

  const clearExistingCartItems = async () => {
    // Clear existing cart items (similar to CartResetter logic)
    await Promise.all(
      ACMStore.CartItems.map(async (CartItem) => {
        ACMStore.remove('CartItem', CartItem)
        if (!CartItem.isNew()) {
          await CartItem.delete()
        }
        return CartItem
      })
    )

    // Clear external carriers
    ACMStore.ExternalCarriers = []

    // Reset cart status to OPEN if needed
    if (ACMStore.Cart && ACMStore.Cart.get('cart_status') !== 'OPEN') {
      ACMStore.Cart.set('cart_status', 'OPEN')
      await ACMStore.Cart.save()
    }

    // Clear packet items
    ACMStore.PacketItems = []
    // Don't clear CartItems here - we'll replace them with new ones
  }

  const getPriorityIndicator = (priority) => {
    switch (priority && `${priority}`.trim().toLowerCase()) {
      case 'core':
        return '$$$'
      case 'preferred':
        return '$$'
      case 'additional':
        return '$'
      default:
        return ''
    }
  }

  const getPriorityTableName = (priority) => {
    switch (priority && `${priority}`.trim().toLowerCase()) {
      case 'core':
        return 'USABG Core Carriers'
      case 'preferred':
        return 'USABG Preferred Carriers'
      case 'additional':
        return 'Additional Carriers'
      default:
        return ''
    }
  }

  const getPriorityTableDescrip = (priority) => {
    switch (priority && `${priority}`.trim().toLowerCase()) {
      case 'core':
        return 'Core carriers provide agents with the best support, service and commissions. All agents are required to have core carriers in their portfolio.'
      case 'preferred':
        return "Preferred carriers provide additional coverage for your client's protection. We highly recommend all agents have these products to better serve their clients."
      case 'additional':
        return 'Additional carriers to expand your portfolio and provide more options for your clients.'
      default:
        return ''
    }
  }

  const renderTable = (priority) => {
    const CarrierGroups = organizePrioritySiblings(loadSelectableCarriers())

    return (
      <MDBCard className="mb-4 carrier-table-card">
        <MDBCardHeader>
          <MDBRow>
            <MDBCol size="12">
              <div>
                <strong>
                  <span className="priority-level-indicator">
                    {getPriorityIndicator(priority)}
                  </span>
                </strong>
                &nbsp;{getPriorityTableName(priority)}
              </div>
              <small>{getPriorityTableDescrip(priority)}</small>
            </MDBCol>
          </MDBRow>
        </MDBCardHeader>
        <MDBCardBody>
          <RenderCarrierTable
            priority={priority}
            Carriers={CarrierGroups[priority]}
            onAddToCart={handleAddToCart}
            selectedCarrierIds={selectedCarrierIds}
          />
        </MDBCardBody>
      </MDBCard>
    )
  }

  return (
    <div id="OnboardingOverrideCartComponent">
      <label className="section-label">
        <MDBIcon icon="cog" />
        &nbsp;BST Carrier Selection Override
      </label>

      <MDBContainer fluid>
        <MDBRow className="mb-3">
          <MDBCol size="12">
            <div className="alert alert-warning">
              <strong>Override Mode Active:</strong> Select a minimum of{' '}
              {requiredItems} carriers for this agent's onboarding. You can
              select more than {requiredItems} carriers if needed. The standard
              carrier priority mechanism has been bypassed.
            </div>
          </MDBCol>
        </MDBRow>

        <MDBRow className="mb-3">
          <MDBCol size="12" md="6">
            <MDBInput
              label="Search carriers..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              icon="search"
            />
          </MDBCol>
          <MDBCol size="12" md="6" className="d-flex align-items-end">
            <div className="selection-counter">
              Selected: {selectedCarrierIds.length} (minimum: {requiredItems})
            </div>
          </MDBCol>
        </MDBRow>

        <MDBRow>
          <MDBCol size="12">
            {renderTable('core')}
            {renderTable('preferred')}
            {renderTable('additional')}
          </MDBCol>
        </MDBRow>

        <MDBRow className="mt-4">
          <MDBCol size="12" className="text-center">
            <MDBBtn
              color="secondary"
              onClick={onCancel}
              disabled={isLoading}
              className="mr-3"
            >
              Go Back
            </MDBBtn>
            <MDBBtn
              color="danger"
              onClick={handleResetSelection}
              disabled={isLoading}
              className="mr-3"
            >
              Reset Override
            </MDBBtn>
            <MDBBtn
              className="save-override"
              onClick={handleSaveSelection}
              disabled={selectedCarrierIds.length < requiredItems || isLoading}
            >
              {isLoading ? (
                <>
                  <MDBIcon icon="spinner" spin className="mr-2" />
                  Saving...
                </>
              ) : (
                <>
                  Save Selection & Continue ({selectedCarrierIds.length}{' '}
                  carriers)
                  <MDBIcon icon="arrow-right" className="ml-2" />
                </>
              )}
            </MDBBtn>
          </MDBCol>
        </MDBRow>
      </MDBContainer>
    </div>
  )
}

export default observer(OnboardingOverrideCart)
